import React, { useEffect, useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Switch,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Snackbar,
    Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import '../css/insuranceAgentNpn.css';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import ScaleLoader from 'react-spinners/ScaleLoader';
import { TableSortLabel } from '@mui/material';

const InsuranceAgentNpn = () => {
    const [agents, setAgents] = useState([]);
    const [open, setOpen] = useState(false);
    const [updateOpen, setUpdateOpen] = useState(false);
    const [newAgent, setNewAgent] = useState({ name: '', npn: '', licenseStatus: 'E' });
    const [oldAgent, setOldAgent] = useState({ oldNpn: '', oldCreatedDate: '', oldID: '' });
    const [loading, setLoading] = useState(true);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [isValid, setIsValid] = useState(false);
    const [order, setOrder] = useState('asc'); // 'asc' or 'desc'
    const [orderBy, setOrderBy] = useState('agentname'); // Default column to sort
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        // setTimeout(() => {
        //     setAgents([
        //         { id: 1, agentname: 'John Doe', npn: '12345', createdDate: '2024-01-01', updatedDate: '2024-01-05', status: 'INACTIVE' },
        //         { id: 2, agentname: 'Jane Smith', npn: '54321', createdDate: '2024-02-01', updatedDate: '2024-02-05', status: 'ACTIVE' },
        //     ]);
        //     setLoading(false);
        // }, 2000); // Simulating a 2-second loading time
        fetchInsuranceAgentData()
    }, []);

    const handleSortRequest = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const sortedAgents = [...agents].sort((a, b) => {
        if (orderBy === 'createdDate' || orderBy === 'updatedDate') {
            return (new Date(a[orderBy]) < new Date(b[orderBy]) ? -1 : 1) * (order === 'asc' ? 1 : -1);
        }
        return (a[orderBy] < b[orderBy] ? -1 : 1) * (order === 'asc' ? 1 : -1);
    });

    const fetchInsuranceAgentData = () => {
        fetch('https://rms.healtheesystems.com:9443/rms-cctools-0.0.1-SNAPSHOT/api/insurance_agent', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        })
            .then(response => response.json())
            .then(data => {
                console.log('Fetched agent data:', data);
                setAgents(data);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching insurance agent data:', error);
                setLoading(false);
            });
    };


    const handleDelete = (npn) => {
        setLoading(true);
        console.log(`Delete agent with NPN: ${npn}`);

        const apiUrl = `https://rms.healtheesystems.com:9443/rms-cctools-0.0.1-SNAPSHOT/api/insurance_agent/${npn}`;

        fetch(apiUrl, {
            method: 'DELETE',
        })
            .then(response => {
                if (response.ok) {
                    console.log(`Agent with NPN ${npn} deleted successfully.`);
                    fetchInsuranceAgentData();
                    setSnackbarMessage('Agent deleted successfully');
                    setSnackbarSeverity('success');
                    setSnackbarOpen(true);
                } else {
                    console.error('Failed to delete agent:', response.statusText);
                    setSnackbarMessage('Failed to delete agent');
                    setSnackbarSeverity('error');
                    setLoading(false);
                    setSnackbarOpen(true);
                }
            })
            .catch(error => {
                console.error('Error deleting agent:', error);
                setSnackbarMessage('Error deleting agent');
                setSnackbarSeverity('error');
                setLoading(false);
                setSnackbarOpen(true);
            });
    };

    const handleClose = () => {
        setOpen(false)
        setUpdateOpen(false)
    };

    const handleCreateAgent = () => {
        setOpen(false)
        setLoading(true);
        const agentData = {
            agentname: newAgent.name,
            npn: newAgent.npn,
            createdDate: new Date().toISOString().split('T')[0],
            status: newAgent.licenseStatus === "ACTIVE" ? "ACTIVE" : "INACTIVE"
        };

        fetch('https://rms.healtheesystems.com:9443/rms-cctools-0.0.1-SNAPSHOT/api/insurance_agent', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(agentData),
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                return response.json();
            })
            .then((data) => {
                console.log('Agent successfully created:', data);
                fetchInsuranceAgentData();
                setSnackbarMessage('Agent created successfully');
                setSnackbarSeverity('success');
                setSnackbarOpen(true);
                resetAgentForm()
            })
            .catch((error) => {
                console.error('Error creating agent:', error);
                setOpen(true)
                setSnackbarMessage('Error creating agent');
                setSnackbarSeverity('error');
                setLoading(false);
                setSnackbarOpen(true);
            });
    };

    const resetAgentForm = () => {
        setNewAgent({
            name: '',
            npn: ''
        });
        enableSuccessIcon({ name: '', npn: '' });  // Ensure to handle validation for empty values
    };

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    const enableSuccessIcon = (e) => {
        if (e.name?.trim() !== '' && e.npn?.trim() !== '') {
            setIsValid(true);
        } else {
            setIsValid(false);
        }
    };

    const handleEdit = (npn) => {
        setUpdateOpen(true)
        fetch(`https://rms.healtheesystems.com:9443/rms-cctools-0.0.1-SNAPSHOT/api/insurance_agent/${npn}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error('Failed to fetch agent data');
                }
                return response.json(); // Parse the response data as JSON
            })
            .then((agentData) => {
                setNewAgent({
                    name: agentData.agentname || '',
                    npn: agentData.npn || '',
                    licenseStatus: agentData.status === 'ACTIVE' ? 'ACTIVE' : 'INACTIVE'
                });
                setOldAgent({
                    oldNpn: npn || '',
                    oldCreatedDate: agentData.createdDate || '',
                    oldID: agentData.id || ''
                });
            })
            .catch((error) => {
                console.error('Error fetching agent data:', error);
            });
    };


    const handleUpdateAgent = () => {
        setUpdateOpen(false);
        const { name, npn, licenseStatus } = newAgent;
        const { oldNpn, oldCreatedDate, oldID } = oldAgent
        // Determine the new status based on the current license status
        const newStatus = licenseStatus === 'ACTIVE' ? 'ACTIVE' : 'INACTIVE';

        const apiUrl = `https://rms.healtheesystems.com:9443/rms-cctools-0.0.1-SNAPSHOT/api/insurance_agent/${oldNpn}`;

        fetch(apiUrl, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id: oldID,
                agentname: name,
                npn: npn,
                createdDate: oldCreatedDate,
                updatedDate: new Date().toISOString().split('T')[0],
                status: newStatus
            }),
        })
            .then(response => {
                if (response.ok) {
                    console.log(`Agent status updated to ${newStatus} for NPN: ${npn}`);
                    fetchInsuranceAgentData(); // Refresh the agent data
                    setSnackbarMessage(`Agent status updated to ${newStatus === "ACTIVE" ? 'Active' : 'INACTIVE'}`);
                    setSnackbarSeverity('success');
                    setSnackbarOpen(true);
                    resetAgentForm(); // Reset the form after update
                } else {
                    console.error('Failed to update agent status:', response.statusText);
                    setSnackbarMessage('Failed to update agent status');
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                    setUpdateOpen(true); // Reopen the dialog if update fails
                }
            })
            .catch(error => {
                console.error('Error updating agent status:', error);
                setSnackbarMessage('Error updating agent status');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            });
    };

    const filteredAgents = agents.filter((agent) => {
        return (
            agent.agentname.toLowerCase().includes(searchQuery.toLowerCase()) ||
            String(agent.npn).includes(searchQuery) ||  // Ensure npn is a string
            String(agent.createdDate).includes(searchQuery) ||  // Ensure createdDate is a string
            String(agent.updatedDate).includes(searchQuery)  // Ensure updatedDate is a string
        );
    });

    const filteredAndSortedAgents = [...agents]
    .filter((agent) => {
        return (
            agent.agentname.toLowerCase().includes(searchQuery.toLowerCase()) ||
            String(agent.npn).includes(searchQuery) ||  // Ensure npn is a string
            String(agent.createdDate).includes(searchQuery) ||  // Ensure createdDate is a string
            String(agent.updatedDate).includes(searchQuery)  // Ensure updatedDate is a string
        );
    })
    .sort((a, b) => {
        if (orderBy === 'createdDate' || orderBy === 'updatedDate') {
            return (new Date(a[orderBy]) < new Date(b[orderBy]) ? -1 : 1) * (order === 'asc' ? 1 : -1);
        }
        return (a[orderBy] < b[orderBy] ? -1 : 1) * (order === 'asc' ? 1 : -1);
    });

    return (
        <div className="insurance-agent-npn">
            <h1 className="title">Insurance Agent Details</h1>

            {loading ? (
                <div className="loader-container">
                    <ScaleLoader color="#AFDBF5" />
                </div>
            ) : (
                <TableContainer component={Paper} className="table-container">
                    {/* <div className="button-container">
                        <button className="create-button" onClick={() => setOpen(true)}>
                            <AddIcon className="add-icon" /> Create
                        </button>
                    </div> */}

                    <div className="action-container">

                        {/* Search Input */}
                        <div className="search-container">
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={handleSearchChange}
                                className="search-input"
                            />
                        </div>
                        <div className="button-container">
                            <button className="create-button" onClick={() => setOpen(true)}>
                                <AddIcon className="add-icon" /> Create
                            </button>
                        </div>
                    </div>
                    <Table className="insurance-table" aria-label="insurance agent table">
                        <TableHead className="table-header">
                            <TableRow>
                                <TableCell className="header-cell" style={{ display: 'none' }}>ID</TableCell>

                                {/* Agent Name */}
                                <TableCell className="header-cell">
                                    <TableSortLabel
                                        active={orderBy === 'agentname'}
                                        direction={orderBy === 'agentname' ? order : 'asc'}
                                        onClick={() => handleSortRequest('agentname')}
                                    >
                                        Agent Name
                                    </TableSortLabel>
                                </TableCell>

                                {/* NPN */}
                                <TableCell className="header-cell">
                                    <TableSortLabel
                                        active={orderBy === 'npn'}
                                        direction={orderBy === 'npn' ? order : 'asc'}
                                        onClick={() => handleSortRequest('npn')}
                                    >
                                        NPN
                                    </TableSortLabel>
                                </TableCell>

                                {/* Created Date */}
                                <TableCell className="header-cell">
                                    <TableSortLabel
                                        active={orderBy === 'createdDate'}
                                        direction={orderBy === 'createdDate' ? order : 'asc'}
                                        onClick={() => handleSortRequest('createdDate')}
                                    >
                                        Created Date
                                    </TableSortLabel>
                                </TableCell>

                                {/* Updated Date */}
                                <TableCell className="header-cell">
                                    <TableSortLabel
                                        active={orderBy === 'updatedDate'}
                                        direction={orderBy === 'updatedDate' ? order : 'asc'}
                                        onClick={() => handleSortRequest('updatedDate')}
                                    >
                                        Updated Date
                                    </TableSortLabel>
                                </TableCell>

                                {/* Actions */}
                                <TableCell className="header-cell">Actions</TableCell>
                            </TableRow>
                        </TableHead>

                        {/* Table Body */}
                        <TableBody className="table-body">
                            {filteredAndSortedAgents.map((agent) => (
                                <TableRow key={agent.npn} className="table-row">
                                    <TableCell className="table-cell" style={{ display: 'none' }}>{agent.id}</TableCell>
                                    <TableCell className="table-cell">{agent.agentname}</TableCell>
                                    <TableCell className="table-cell">{agent.npn}</TableCell>
                                    <TableCell className="table-cell">{agent.createdDate}</TableCell>
                                    <TableCell className="table-cell">{agent.updatedDate}</TableCell>
                                    <TableCell className="table-cell">
                                        <IconButton onClick={() => handleEdit(agent.id)} aria-label="edit" className="icon-button">
                                            <EditIcon className="edit-icon" />
                                        </IconButton>
                                        <IconButton onClick={() => handleDelete(agent.id)} aria-label="delete" className="icon-button">
                                            <DeleteIcon className="delete-icon" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

            )}

            <Dialog open={open} onClose={() => setOpen(false)} classes={{ paper: 'dialog-container' }}>
                <DialogTitle className="dialog-title">Create New Agent</DialogTitle>
                <DialogContent className="dialog-content">
                    <TextField
                        autoFocus
                        margin="dense"
                        id="name"
                        label="Agent Name"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={newAgent.name}
                        onChange={(e) => {
                            const updatedAgent = { ...newAgent, name: e.target.value };
                            setNewAgent(updatedAgent);
                            enableSuccessIcon(updatedAgent);
                        }}
                        className="dialog-text-field"
                    />
                    <TextField
                        margin="dense"
                        id="npn"
                        label="NPN"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={newAgent.npn}
                        onChange={(e) => {
                            const updatedAgent = { ...newAgent, npn: e.target.value };
                            setNewAgent(updatedAgent);
                            enableSuccessIcon(updatedAgent);
                        }}
                        className="dialog-text-field"
                    />
                    <div className={`dialog-switch-container ${newAgent.licenseStatus === 'E' ? 'ACTIVE' : 'INACTIVE'}`}>
                        <span>Status: {newAgent.licenseStatus === 'ACTIVE' ? 'ACTIVE' : 'INACTIVE'}</span>
                        <Switch
                            checked={newAgent.licenseStatus === "ACTIVE"}
                            onChange={() => setNewAgent((prev) => ({ ...prev, licenseStatus: prev.licenseStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE' }))}
                            color="primary"
                        />
                    </div>
                </DialogContent>
                <DialogActions className="dialog-actions">
                    <IconButton onClick={handleClose} className={`cancel-icon`} aria-label="cancel">
                        <CancelIcon color="error" />
                    </IconButton>
                    {isValid && (
                        <IconButton
                            onClick={handleCreateAgent}
                            className="dialog-button"
                            aria-label="create"
                        >
                            <CheckIcon color="success" />
                        </IconButton>
                    )}
                </DialogActions>
            </Dialog>

            <Dialog open={updateOpen} onClose={() => setUpdateOpen(false)} classes={{ paper: 'dialog-container' }}>
                <DialogTitle className="dialog-title">Edit Agent</DialogTitle>
                <DialogContent className="dialog-content">
                    <TextField
                        autoFocus
                        margin="dense"
                        id="updateName"
                        label="Agent Name"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={newAgent.name}
                        onChange={(e) => {
                            const updatedAgent = { ...newAgent, name: e.target.value };
                            setNewAgent(updatedAgent);
                            enableSuccessIcon(updatedAgent);
                        }}
                        className="dialog-text-field"
                    />
                    <TextField
                        margin="dense"
                        id="updateNpn"
                        label="NPN"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={newAgent.npn}
                        onChange={(e) => {
                            const updatedAgent = { ...newAgent, npn: e.target.value };
                            setNewAgent(updatedAgent);
                            enableSuccessIcon(updatedAgent);
                        }}
                        className="dialog-text-field"
                    />
                    <div className={`dialog-switch-container ${newAgent.licenseStatus === 'ACTIVE' ? 'ACTIVE' : 'INACTIVE'}`}>
                        <span>Status: {newAgent.licenseStatus === 'ACTIVE' ? 'ACTIVE' : 'INACTIVE'}</span>
                        <Switch
                            checked={newAgent.licenseStatus === "ACTIVE"}
                            onChange={() => setNewAgent((prev) => ({ ...prev, licenseStatus: prev.licenseStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE' }))}
                            color="primary"
                        />
                    </div>
                </DialogContent>
                <DialogActions className="dialog-actions">
                    <IconButton onClick={handleClose} className={`cancel-icon`} aria-label="cancel">
                        <CancelIcon color="error" />
                    </IconButton>
                    <IconButton
                        onClick={handleUpdateAgent}
                        className="dialog-button"
                        aria-label="create"
                    >
                        <CheckIcon color="success" />
                    </IconButton>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default InsuranceAgentNpn;
